import { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { Search, Filter, MoreVertical, ExternalLink, Plus, BarChart3, Key, Eye, Ban, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { tokenStore } from "@/lib/api";

const planColor = (p) => {
  const s = (p || "").toLowerCase();
  return s === 'enterprise' ? 'bg-slate-900 text-white' : s === 'business' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' : 'bg-slate-50 text-slate-600 border-slate-200';
};

export default function TenantManagement() {
  const { t } = useLanguage();
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planFilter, setPlanFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${tokenStore.getAccess()}` };
        const base = process.env.REACT_APP_BACKEND_URL;
        const [tRes, pRes] = await Promise.all([
          fetch(`${base}/api/v1/tenants/?limit=200`, { headers }),
          fetch(`${base}/api/v1/plans/`, { headers }),
        ]);
        if (tRes.ok) setTenants(await tRes.json());
        if (pRes.ok) setPlans(await pRes.json());
      } catch { /* fall back to empty */ }
      finally { setLoading(false); }
    })();
  }, []);

  const planName = (planId) => plans.find(p => p.id === planId)?.name || planId?.slice(0, 8) || "—";
  const planPrice = (planId) => plans.find(p => p.id === planId)?.price_monthly ?? null;

  const displayTenants = tenants.filter(t => {
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.owner_email.toLowerCase().includes(search.toLowerCase());
    const matchPlan = planFilter === "all" || planName(t.plan_id).toLowerCase() === planFilter;
    const matchStatus = statusFilter === "all" || t.status.toLowerCase() === statusFilter;
    return matchSearch && matchPlan && matchStatus;
  });

  // Summary counts
  const countByPlan = (slug) => tenants.filter(t => planName(t.plan_id).toLowerCase() === slug).length;
  const totalMRR = tenants.reduce((sum, t) => sum + (planPrice(t.plan_id) || 0), 0);

  return (
    <div data-testid="tenant-management-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="tenant-mgmt-title">{t('tenant_mgmt.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">Manage customer organizations, plans, and usage across the platform</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right mr-4">
            <div className="text-xs text-slate-500">Total MRR</div>
            <div className="text-lg font-bold text-slate-900 font-mono">${totalMRR.toLocaleString()}</div>
          </div>
          <Link to="/admin/tenants/new" className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 transition-colors text-xs font-bold uppercase tracking-widest px-5 py-2.5" data-testid="add-tenant-btn">
            <Plus className="w-4 h-4" /> Add New Tenant
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4" data-testid="tenant-filters">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tenants..." className="pl-9 h-9 bg-white border-slate-200 text-sm" data-testid="tenant-search" />
        </div>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-36 h-9 text-xs" data-testid="tenant-plan-filter">
            <Filter className="w-3.5 h-3.5 mr-2 text-slate-400" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Plans</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
            <SelectItem value="business">Business</SelectItem>
            <SelectItem value="developer">Developer</SelectItem>
            <SelectItem value="free">Free</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-9 text-xs" data-testid="tenant-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        {[
          { label: "Total Tenants", value: loading ? "…" : tenants.length, sub: `${displayTenants.length} shown` },
          { label: "Enterprise", value: loading ? "…" : countByPlan("enterprise"), sub: "" },
          { label: "Business", value: loading ? "…" : countByPlan("business"), sub: "" },
          { label: "Developer", value: loading ? "…" : countByPlan("developer"), sub: "" },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-slate-200 p-4" data-testid={`tenant-summary-${i}`}>
            <div className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</div>
            <div className="text-xl font-bold text-slate-900">{s.value}</div>
            <div className="text-[10px] text-slate-400">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Tenant Table */}
      <div className="bg-white border border-slate-200" data-testid="tenant-table">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading tenants...
          </div>
        ) : (
        <div>
          <div className="grid grid-cols-[1fr_100px_80px_140px_100px_80px_90px_44px] bg-slate-50 border-b border-slate-200">
            {["Organization","Plan","Status","API Calls (Month)","Bandwidth","MRR","Created",""].map((h,i) => (
              <div key={i} className="px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
            ))}
          </div>
          {displayTenants.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400">No tenants found</div>
          ) : displayTenants.map((t, i) => (
            <div key={t.id} className="grid grid-cols-[1fr_100px_80px_140px_100px_80px_90px_44px] border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`tenant-row-${i}`}>
              <div className="px-4 py-3 flex items-center">
                <Link to={`/admin/tenants/${t.id}`} className="hover:underline" data-testid={`tenant-name-link-${i}`}>
                  <div className="text-sm font-medium text-slate-900">{t.name}</div>
                  <div className="text-[10px] font-mono text-slate-400">{t.owner_email}</div>
                </Link>
              </div>
              <div className="px-4 py-3 flex items-center"><Badge className={`text-[10px] ${planColor(planName(t.plan_id))}`}>{planName(t.plan_id)}</Badge></div>
              <div className="px-4 py-3 flex items-center">
                <Badge variant="outline" className={`text-[10px] uppercase ${t.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{t.status}</Badge>
              </div>
              <div className="px-4 py-3 font-mono text-xs text-slate-600 flex items-center">{(t.api_calls_current_month || 0).toLocaleString()}</div>
              <div className="px-4 py-3 font-mono text-xs text-slate-600 flex items-center">{(t.bandwidth_gb || 0).toFixed(2)} GB</div>
              <div className="px-4 py-3 font-mono text-xs font-medium text-slate-900 flex items-center">
                {planPrice(t.plan_id) != null ? `$${planPrice(t.plan_id)}` : "—"}
              </div>
              <div className="px-4 py-3 text-xs text-slate-500 flex items-center">{t.created_at ? t.created_at.slice(0, 10) : "—"}</div>
              <div className="px-4 py-3 flex items-center">
                <DropdownMenu>
                  <DropdownMenuTrigger className="p-1 text-slate-400 hover:text-slate-600" data-testid={`tenant-actions-${i}`}>
                    <MoreVertical className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-xs" asChild>
                      <Link to={`/admin/tenants/${t.id}`}><Eye className="w-3 h-3 mr-2" />View Details</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-xs" asChild>
                      <Link to={`/admin/tenants/${t.id}/usage`}><BarChart3 className="w-3 h-3 mr-2" />View Usage</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-xs" asChild>
                      <Link to={`/admin/tenants/${t.id}/keys`}><Key className="w-3 h-3 mr-2" />Manage Keys</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-xs text-red-600"><Ban className="w-3 h-3 mr-2" />Suspend Tenant</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    </div>
  );
}
